import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Loader2,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

import api from "../lib/api";
import { COMPANY_INFO } from "../data/mock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

// Validation schema using the project's existing zod stack
const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email address is required")
    .email("Enter a valid email address"),
  password: z
    .string()
    .min(1, "Password is required")
    .min(8, "Password must be at least 8 characters"),
  remember: z.boolean().optional(),
});

// Brand value props re-used from the site's positioning
const HIGHLIGHTS = [
  {
    icon: Sparkles,
    title: "AI-native products",
    description: "Intelligence designed into everything we build.",
  },
  {
    icon: Users,
    title: "Your team. Your outcomes.",
    description: "A long-term engineering partner, not a dev shop.",
  },
  {
    icon: ShieldCheck,
    title: "Enterprise-grade security",
    description: "Your data is protected end-to-end.",
  },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "", remember: false },
    mode: "onBlur",
  });

  const remember = watch("remember");


  const onSubmit = async (data) => {
    try {
      const response = await api.post("/login", {
        email: data.email,
        password: data.password,
      });

      const result = response.data;

      // Store token
      localStorage.setItem("token", result.token);

      // Store user data
      localStorage.setItem("user", JSON.stringify(result.user));

      toast.success(result.message || "Login successful");

      navigate("/admin/blogs");
    } catch (error) {
      // axios puts the server's response inside error.response
      const result = error.response ? error.response.data : null;

      if (result && result.errors) {
        Object.values(result.errors).forEach((messages) => {
          toast.error(messages[0]);
        });
      } else if (result && result.message) {
        toast.error(result.message);
      } else {
        toast.error("Server error. Please try again later.");
      }
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] md:min-h-[calc(100vh-5rem)] grid lg:grid-cols-2 bg-white">
      {/* ---------- Visual / Branding panel ---------- */}
      <section
        className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-12 xl:p-16"
        aria-hidden="true"
      >
        {/* Background image with brand overlay (same hero treatment as the site) */}
        <img
          src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=1600&q=80"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/40" />
        {/* Decorative brand glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#b32126]/30 to-rose-500/10 rounded-full -mr-32 -mt-32 blur-3xl" />

        <div className="relative z-10">
          <span className="inline-flex items-center rounded-xl bg-white px-4 py-2.5 shadow-md">
            <img
              src={COMPANY_INFO.logo}
              alt={COMPANY_INFO.name}
              className="h-8 w-auto"
            />
          </span>
        </div>

        <div className="relative z-10 max-w-md">
          <span className="text-[#ff5a5f] font-semibold tracking-wide">
            Welcome back
          </span>
          <h2 className="mt-3 text-4xl xl:text-5xl font-bold text-white leading-tight">
            Build products that scale.
          </h2>
          <p className="mt-4 text-lg text-gray-300">
            Sign in to your {COMPANY_INFO.name} workspace and pick up right
            where you left off.
          </p>

          <ul className="mt-10 space-y-6">
            {HIGHLIGHTS.map(({ icon: Icon, title, description }) => (
              <li key={title} className="flex items-start gap-4">
                <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/15">
                  <Icon className="h-5 w-5 text-[#ff5a5f]" />
                </span>
                <div>
                  <h3 className="font-semibold text-white">{title}</h3>
                  <p className="text-sm text-gray-400">{description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative z-10 text-sm text-gray-400">
          © {COMPANY_INFO.name}. {COMPANY_INFO.tagline}
        </p>
      </section>

      {/* ---------- Form panel ---------- */}
      <section className="flex items-center justify-center px-4 sm:px-6 lg:px-12 py-12">
        <div className="w-full max-w-md animate-fadeIn">
          {/* Logo for small screens (visual panel hidden) */}
          <div className="lg:hidden mb-8 flex justify-center">
            <img
              src={COMPANY_INFO.logo}
              alt={COMPANY_INFO.name}
              className="h-9 w-auto"
            />
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Sign in</h1>
            <p className="mt-2 text-gray-600">
              Enter your credentials to access your account.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-gray-700">
                Email address
              </Label>
              <div className="relative mt-2">
                <Mail
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  className={cn(
                    "h-12 pl-10 text-base rounded-lg border-gray-200 focus-visible:ring-2 focus-visible:ring-[#b32126] focus-visible:ring-offset-0",
                    errors.email &&
                      "border-destructive focus-visible:ring-destructive"
                  )}
                  {...register("email")}
                />
              </div>
              {errors.email && (
                <p
                  id="email-error"
                  role="alert"
                  className="mt-1.5 text-sm text-destructive"
                >
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-gray-700">
                  Password
                </Label>
                <Link
                  to="/contact"
                  className="text-sm font-medium text-[#b32126] hover:text-[#8f1a1f] hover:underline transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-2">
                <Lock
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  aria-invalid={!!errors.password}
                  aria-describedby={
                    errors.password ? "password-error" : undefined
                  }
                  className={cn(
                    "h-12 pl-10 pr-11 text-base rounded-lg border-gray-200 focus-visible:ring-2 focus-visible:ring-[#b32126] focus-visible:ring-offset-0",
                    errors.password &&
                      "border-destructive focus-visible:ring-destructive"
                  )}
                  {...register("password")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-400 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b32126]"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p
                  id="password-error"
                  role="alert"
                  className="mt-1.5 text-sm text-destructive"
                >
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="remember"
                checked={remember}
                onCheckedChange={(checked) =>
                  setValue("remember", checked === true)
                }
              />
              <Label
                htmlFor="remember"
                className="text-sm font-normal text-gray-600 cursor-pointer"
              >
                Remember me for 30 days
              </Label>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 text-base font-semibold rounded-lg bg-[#b32126] hover:bg-[#8f1a1f] shadow-lg hover:shadow-xl transition-all"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            Don&apos;t have an account?{" "}
            <Link
              to="/contact"
              className="font-semibold text-[#b32126] hover:text-[#8f1a1f] hover:underline transition-colors"
            >
              Get in touch
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default LoginPage;
